import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "components/layout/Header";
import Footer from "components/layout/Footer";
import {
  Shield,
  Eye,
  Cookie,
  Globe,
  Lock,
  ShieldCheck,
  AlertTriangle,
  Fingerprint,
  Server,
  UserX,
  Scale,
  Bot,
  Sparkles,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";

/* ═══════════════ ANIMATIONS ═══════════════ */

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

/* ═══════════════ DATA ═══════════════ */

const PRIVACY_SECTIONS = [
  {
    icon: Eye,
    title: "What We Collect (Don't Panic)",
    emoji: "👀",
    content: `Relax. We're not the CIA. When you visit our website, we might collect some basic stuff like your name, email, and which pages you clicked on. You know, the usual "we need to know you exist" kind of data. We don't read your diary, we don't know your Netflix password, and we definitely don't care about your search history (we have our own questionable browsing habits to worry about).

If you fill out a form, we collect what you typed. Shocking, right? If you email us, we keep the email. If you call us, we might remember your voice. That's about it.`,
    gradient: "from-violet-500 to-purple-600",
    light: "bg-violet-50",
  },
  {
    icon: Cookie,
    title: "Cookies (The Digital Kind, Sadly)",
    emoji: "🍪",
    content: `Yes, we use cookies. No, not the chocolate chip ones — though we wish. These are tiny files that sit on your device and help us understand how you use our site. They're harmless. Probably more harmless than the actual cookies you ate today.

We use analytics cookies to see if anyone actually reads this page (spoiler: you're one of the brave few). We also use functional cookies so the site doesn't forget you exist every time you blink. You can disable cookies in your browser settings, but then the website might act like it has amnesia. Your call.`,
    gradient: "from-purple-500 to-indigo-600",
    light: "bg-purple-50",
  },
  {
    icon: Lock,
    title: "How We Protect Your Data",
    emoji: "🔐",
    content: `We use industry-standard security measures. Translation: we don't store your passwords on a Post-it note. Your data is encrypted, our servers are as secure as we can make them, and our team has been explicitly told that "password123" is not an acceptable security practice.

That said, no system is 100% secure. If the entire internet collapses, we've got bigger problems than your email address. We do our best, and our best is pretty good. Not "Fort Knox" good, but "we actually care about this stuff" good.`,
    gradient: "from-indigo-500 to-blue-600",
    light: "bg-indigo-50",
  },
  {
    icon: Server,
    title: "Where Your Data Lives",
    emoji: "🏠",
    content: `Your data lives on servers. Surprise! These servers might be in India, or they might be in the cloud (which is just someone else's computer, let's be real). We use reputable hosting providers who promise not to peek at your data, and we hold them to that.

We don't sell your data. We don't trade it. We don't use it to train an AI that will eventually replace us (oh wait... more on that later). Your data stays with us unless the law says otherwise, or unless you explicitly ask us to share it, in which case — are you okay?`,
    gradient: "from-fuchsia-500 to-pink-600",
    light: "bg-fuchsia-50",
  },
  {
    icon: UserX,
    title: "Third Parties (The Necessary Evil)",
    emoji: "🤝",
    content: `We work with some third-party services — analytics tools, payment processors, that sort of thing. They might get a slice of your data, but only what they need to do their job. Think of them as contractors: they come in, do the work, and leave. They don't get to redecorate.

These third parties have their own privacy policies, and we strongly recommend reading them if you enjoy light fiction. We can't control what they do with the data once they have it, but we pick partners who don't act like data-hungry supervillains.`,
    gradient: "from-violet-500 to-fuchsia-600",
    light: "bg-violet-50",
  },
  {
    icon: Globe,
    title: "International Users (Yes, Laws Exist Everywhere)",
    emoji: "🌍",
    content: `BusiGrow operates in India, the Middle East, and Southeast Asia. Different countries have different data protection laws, and we try to respect all of them simultaneously without developing a legal migraine.

**For EU visitors:** We respect GDPR. You have the right to access, rectify, delete, and throw your data around like confetti. Just email us.
**For Indian users:** We comply with the Digital Personal Data Protection Act (DPDPA). Your data is handled as the law demands.
**For everyone else:** We follow reasonable data protection practices. If your country has specific laws, we'll do our best to comply. We're not lawyers, but we play ones on this website.`,
    gradient: "from-emerald-500 to-teal-600",
    light: "bg-emerald-50",
  },
  {
    icon: Bot,
    title: "AI & This Website (The Honest Part)",
    emoji: "🤖",
    content: `Full transparency: this website was built with the assistance of AI tools. Yes, a robot helped make this. The irony of an AI writing a privacy policy about data protection is not lost on us.

The AI helped with code, design suggestions, and possibly this very paragraph (we'll never tell). But every decision, every pixel, and every questionable joke was approved by an actual human being at BusiGrow. The AI is the tool; we're the craftspeople. Think of it as a very sophisticated hammer that occasionally writes CSS.`,
    gradient: "from-amber-500 to-orange-600",
    light: "bg-amber-50",
  },
  {
    icon: Scale,
    title: "Your Rights (You Actually Have Some)",
    emoji: "⚖️",
    content: `You have the right to:
• **Access** your data — ask us what we have, and we'll tell you (it's probably just your email).
• **Delete** your data — say the word and poof, it's gone. Like it never happened.
• **Correct** your data — if we spelled your name wrong, let us know. We're embarrassed already.
• **Opt out** — unsubscribe from emails, disable cookies, ghost us entirely. We'll be sad but we'll survive.
• **Complain** — if you think we messed up, you can file a complaint with your local data protection authority. Or just send us an angry email. We read those too.`,
    gradient: "from-rose-500 to-red-600",
    light: "bg-rose-50",
  },
];

/* ═══════════════ SECTION COMPONENT ═══════════════ */

function PolicySection({ section, index }) {
  const [expanded, setExpanded] = useState(true);
  const Icon = section.icon;

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      variants={fadeUp}
      className="group"
    >
      <motion.div
        whileHover={{ y: -4, scale: 1.005 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="card-shine glass-strong rounded-3xl p-6 sm:p-8 shadow-apple hover:shadow-apple-xl transition-all duration-500"
      >
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-start sm:items-center gap-4 text-left"
        >
          <div className={`shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br ${section.gradient} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-2xl">{section.emoji}</span>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                {section.title}
              </h3>
            </div>
            <p className="text-xs text-slate-400 mt-1">Section {index + 1} of {PRIVACY_SECTIONS.length}</p>
          </div>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="shrink-0 w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center"
          >
            <ChevronDown className="w-4 h-4 text-violet-500" />
          </motion.div>
        </button>

        {/* Content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="prose prose-slate prose-sm max-w-none text-slate-600 leading-relaxed whitespace-pre-line">
                  {section.content.split("**").map((part, i) =>
                    i % 2 === 1 ? (
                      <strong key={i} className="text-slate-800 font-semibold">{part}</strong>
                    ) : (
                      <span key={i}>{part}</span>
                    )
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ HERO ═══════════════ */

function PrivacyHero() {
  return (
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-24 hero-mesh">
      {/* Background decorations */}
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-violet-200/30 animate-morph-blob" />
      <div className="absolute bottom-0 -left-20 w-60 h-60 rounded-full bg-purple-200/20 animate-morph-blob" style={{ animationDelay: "-5s" }} />
      <div className="absolute top-20 right-1/4 w-16 h-16 rounded-full bg-violet-200/40 animate-float-orb" />
      <div className="absolute bottom-10 left-1/4 w-10 h-10 rounded-full bg-emerald-200/40 animate-float-orb" style={{ animationDelay: "2s" }} />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          {/* Badge */}
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-6">
            <Shield className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-medium text-slate-600">Last Updated: March 2026</span>
          </motion.div>

          {/* Title */}
          <motion.h1
            variants={fadeUp}
            className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6"
          >
            <span className="text-slate-900">Privacy Policy</span>
            <br />
            <span className="text-gradient-vibrant">(The Fun Version)</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p variants={fadeUp} className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed mb-4">
            We know you're not going to read all of this. Nobody does. But we wrote it anyway because lawyers exist and they need to feel useful.
          </motion.p>

          <motion.p variants={fadeUp} className="text-sm text-slate-400 max-w-xl mx-auto">
            TL;DR — We collect minimal data, we don't sell it, we built this site with AI help, and we're compliant with basically every privacy law we could find. Now here's the long version:
          </motion.p>

          {/* Floating decoration */}
          <motion.div
            variants={fadeUp}
            className="mt-8 inline-flex items-center gap-2 glass-strong rounded-2xl px-6 py-3 shadow-apple"
          >
            <Fingerprint className="w-5 h-5 text-violet-500" />
            <span className="text-sm font-medium text-slate-700">Your data is safe with us. Probably.</span>
            <span className="text-lg">😉</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════ MAIN PAGE ═══════════════ */

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
      <Header />

      <PrivacyHero />

      {/* Policy Sections */}
      <section className="relative py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {PRIVACY_SECTIONS.map((section, i) => (
              <PolicySection key={i} section={section} index={i} />
            ))}
          </div>

          {/* Bottom disclaimer */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-16 text-center"
          >
            <div className="glass-strong rounded-3xl p-8 shadow-apple card-shine">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <ShieldCheck className="w-7 h-7 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Still Here? Impressive.</h3>
              <p className="text-slate-500 max-w-lg mx-auto text-sm leading-relaxed mb-4">
                If you actually read all of that, congratulations — you're in the top 0.01% of internet users. 
                We respect your dedication to privacy literacy. If you have any questions, concerns, or just want 
                to tell us how funny (or unfunny) this was, hit us up.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-sm rounded-2xl shadow-apple-lg hover:shadow-apple-xl hover:scale-[1.02] transition-all duration-300"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="mt-6 text-xs text-slate-400">
                This policy is effective as of March 2026 and applies to all users globally. 
                We reserve the right to update this policy, and when we do, we'll make the new version 
                equally entertaining. BusiGrow complies with GDPR (EU), DPDPA (India), PDPA (Singapore/Thailand), 
                CCPA (California), and generally tries not to break any laws anywhere.
              </p>
            </div>
          </motion.div>

          {/* Link to Terms */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <Link to="/terms" className="text-sm text-violet-500 hover:text-violet-700 underline underline-offset-4 transition-colors">
              Read our equally entertaining Terms & Conditions →
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
